import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  Plus, Trash, PaperPlaneTilt, CircleNotch, Package,
  CheckCircle, Clock, Warning, X, Envelope, CurrencyDollar,
  ArrowRight, CaretDown, CaretUp, Receipt, FileText
} from '@phosphor-icons/react';

// ==================== PURCHASE ORDERS TAB ====================
export function PurchaseOrdersTab({ project, onRefresh, projectId }) {
  const [pos, setPOs] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/construction/purchase-orders?project_id=${projectId}`, { withCredentials: true })
      .then(r => setPOs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  const refresh = async () => {
    const r = await axios.get(`${API_URL}/api/construction/purchase-orders?project_id=${projectId}`, { withCredentials: true });
    setPOs(r.data);
  };

  const deletePO = async (id) => {
    if (!window.confirm('Eliminar esta orden de compra?')) return;
    await axios.delete(`${API_URL}/api/construction/purchase-orders/${id}`, { withCredentials: true });
    refresh();
  };

  const sendPO = async (id) => {
    try {
      await axios.post(`${API_URL}/api/construction/purchase-orders/${id}/send`, {}, { withCredentials: true });
      alert('Orden de compra enviada al proveedor');
      refresh();
    } catch (err) { alert(err.response?.data?.detail || 'Error enviando'); }
  };

  const totalPOs = pos.reduce((s, p) => s + (p.total || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-4">
          <div className="bg-white rounded-xl border border-slate-200 px-4 py-3"><p className="text-xs text-slate-500">Total POs</p><p className="text-xl font-bold text-slate-700">{pos.length}</p></div>
          <div className="bg-white rounded-xl border border-slate-200 px-4 py-3"><p className="text-xs text-slate-500">Monto Total</p><p className="text-xl font-bold text-emerald-600">${totalPOs.toLocaleString()}</p></div>
        </div>
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid="add-po-btn"><Plus size={18} /> Nueva Orden de Compra</button>
      </div>

      {showAdd && <POForm projectId={projectId} phases={project.phases || []} onClose={() => setShowAdd(false)} onSave={() => { setShowAdd(false); refresh(); }} />}

      {loading ? <div className="text-center py-8"><CircleNotch size={30} className="animate-spin text-blue-600 mx-auto" /></div> :
        pos.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500"><Package size={36} className="mx-auto mb-2 text-slate-300" /><p className="text-sm">No hay ordenes de compra</p></div> :
        <div className="space-y-3">
          {pos.map(po => <POCard key={po.id} po={po} phases={project.phases || []} onDelete={deletePO} onSend={sendPO} onRefresh={refresh} />)}
        </div>}
    </div>
  );
}

const PO_STATUS = { draft: { l: 'Borrador', c: 'bg-slate-100 text-slate-600' }, sent: { l: 'Enviada', c: 'bg-blue-100 text-blue-700' }, received: { l: 'Recibida', c: 'bg-emerald-100 text-emerald-700' }, partial: { l: 'Parcial', c: 'bg-amber-100 text-amber-700' }, cancelled: { l: 'Cancelada', c: 'bg-red-100 text-red-700' } };

function POCard({ po, phases, onDelete, onSend, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const s = PO_STATUS[po.status] || PO_STATUS.draft;
  const phaseName = phases.find(p => p.id === po.phase_id)?.name || '';

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`po-card-${po.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center"><Package size={20} className="text-indigo-600" /></div>
          <div>
            <div className="flex items-center gap-2"><span className="font-semibold text-slate-900">{po.po_number}</span><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.c}`}>{s.l}</span></div>
            <p className="text-sm text-slate-500">{po.vendor_name}{phaseName ? ` | ${phaseName}` : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-lg font-bold text-slate-900">${po.total.toLocaleString()}</span>
          <div className="flex items-center gap-1">
            {po.status === 'draft' && <button onClick={() => onSend(po.id)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Enviar al proveedor"><PaperPlaneTilt size={16} /></button>}
            <button onClick={() => setExpanded(!expanded)} className="p-1.5 hover:bg-slate-100 rounded">{expanded ? <CaretUp size={16} /> : <CaretDown size={16} />}</button>
            <button onClick={() => onDelete(po.id)} className="p-1.5 text-red-400 hover:bg-red-50 rounded"><Trash size={16} /></button>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t">
          <table className="w-full text-sm">
            <thead className="bg-slate-50"><tr><th className="text-left p-2">Item</th><th className="text-center p-2">Qty</th><th className="text-right p-2">Precio</th><th className="text-right p-2">Total</th><th className="text-center p-2">Recibido</th></tr></thead>
            <tbody>{po.items.map((item, i) => (
              <tr key={i} className="border-t border-slate-100"><td className="p-2">{item.description}</td><td className="p-2 text-center">{item.quantity}</td><td className="p-2 text-right">${item.unit_price}</td><td className="p-2 text-right">${item.total}</td><td className="p-2 text-center">{item.received_qty}/{item.quantity}</td></tr>
            ))}</tbody>
          </table>
          <div className="flex justify-between mt-2 text-sm text-slate-500"><span>Subtotal: ${po.subtotal} | Tax: ${po.tax}</span>{po.notes && <span>Notas: {po.notes}</span>}</div>
        </div>
      )}
    </div>
  );
}

function POForm({ projectId, phases, onClose, onSave }) {
  const [form, setForm] = useState({ vendor_name: '', vendor_email: '', vendor_phone: '', phase_id: '', delivery_date: '', tax_rate: 0, notes: '' });
  const [items, setItems] = useState([{ description: '', quantity: 1, unit_price: 0 }]);
  const [saving, setSaving] = useState(false);

  const addItem = () => setItems([...items, { description: '', quantity: 1, unit_price: 0 }]);
  const updateItem = (i, f, v) => { const u = [...items]; u[i] = { ...u[i], [f]: v }; setItems(u); };
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const subtotal = items.reduce((s, i) => s + (i.quantity * i.unit_price), 0);

  const handleSave = async () => {
    if (!form.vendor_name.trim()) return alert('Proveedor requerido');
    if (items.length === 0) return alert('Agrega al menos un item');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/construction/purchase-orders`, { ...form, project_id: projectId, items }, { withCredentials: true });
      onSave();
    } catch (err) { alert('Error'); } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-blue-200 p-5 space-y-4" data-testid="po-form">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div><label className="block text-xs text-slate-500 mb-1">Proveedor *</label><input value={form.vendor_name} onChange={e => setForm(f => ({...f, vendor_name: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm" placeholder="Nombre del proveedor" data-testid="po-vendor-input" /></div>
        <div><label className="block text-xs text-slate-500 mb-1">Email Proveedor</label><input value={form.vendor_email} onChange={e => setForm(f => ({...f, vendor_email: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm" placeholder="email@proveedor.com" /></div>
        <div><label className="block text-xs text-slate-500 mb-1">Fase</label><select value={form.phase_id} onChange={e => setForm(f => ({...f, phase_id: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm"><option value="">General</option>{phases.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
        <div><label className="block text-xs text-slate-500 mb-1">Fecha Entrega</label><input type="date" value={form.delivery_date} onChange={e => setForm(f => ({...f, delivery_date: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs text-slate-500 mb-1">Tax %</label><input type="number" value={form.tax_rate} onChange={e => setForm(f => ({...f, tax_rate: parseFloat(e.target.value)||0}))} className="w-full border rounded px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs text-slate-500 mb-1">Notas</label><input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm" /></div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2"><label className="text-sm font-medium text-slate-700">Items ({items.length})</label><button onClick={addItem} className="text-xs text-blue-600" data-testid="add-po-item"><Plus size={14} className="inline" /> Agregar</button></div>
        <div className="space-y-2">
          {items.map((item, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input value={item.description} onChange={e => updateItem(i, 'description', e.target.value)} className="flex-1 border rounded px-2 py-1.5 text-sm" placeholder="Descripcion del material" />
              <input type="number" value={item.quantity} onChange={e => updateItem(i, 'quantity', parseFloat(e.target.value)||0)} className="w-20 border rounded px-2 py-1.5 text-sm" placeholder="Qty" />
              <div className="relative w-24"><span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span><input type="number" value={item.unit_price} onChange={e => updateItem(i, 'unit_price', parseFloat(e.target.value)||0)} className="w-full border rounded pl-5 pr-2 py-1.5 text-sm" /></div>
              <span className="text-sm font-medium w-20 text-right">${(item.quantity * item.unit_price).toFixed(2)}</span>
              <button onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600"><X size={16} /></button>
            </div>
          ))}
        </div>
        <div className="text-right mt-2 text-sm font-semibold text-slate-700">Subtotal: ${subtotal.toFixed(2)}</div>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded">Cancelar</button>
        <button onClick={handleSave} disabled={saving} className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50" data-testid="save-po-btn">{saving ? 'Guardando...' : 'Crear PO'}</button>
      </div>
    </div>
  );
}

// ==================== RFIs & CHANGE ORDERS TAB ====================
export function RFIsChangeOrdersTab({ project, onRefresh, projectId }) {
  const [rfis, setRFIs] = useState([]);
  const [cos, setCOs] = useState([]);
  const [showRFI, setShowRFI] = useState(false);
  const [showCO, setShowCO] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('rfis');

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/construction/rfis?project_id=${projectId}`, { withCredentials: true }),
      axios.get(`${API_URL}/api/construction/change-orders?project_id=${projectId}`, { withCredentials: true })
    ]).then(([r, c]) => { setRFIs(r.data); setCOs(c.data); }).catch(() => {}).finally(() => setLoading(false));
  }, [projectId]);

  const refresh = async () => {
    const [r, c] = await Promise.all([
      axios.get(`${API_URL}/api/construction/rfis?project_id=${projectId}`, { withCredentials: true }),
      axios.get(`${API_URL}/api/construction/change-orders?project_id=${projectId}`, { withCredentials: true })
    ]);
    setRFIs(r.data); setCOs(c.data);
  };

  const respondRFI = async (rfiId, response) => {
    await axios.post(`${API_URL}/api/construction/rfis/${rfiId}/respond`, { response }, { withCredentials: true });
    refresh();
  };

  const convertToCO = async (rfiId, amount, impactDays, reason) => {
    await axios.post(`${API_URL}/api/construction/rfis/${rfiId}/convert-to-co`, { amount, impact_days: impactDays, reason }, { withCredentials: true });
    refresh();
  };

  const approveCO = async (coId) => { await axios.post(`${API_URL}/api/construction/change-orders/${coId}/approve`, {}, { withCredentials: true }); refresh(); };
  const rejectCO = async (coId) => { await axios.post(`${API_URL}/api/construction/change-orders/${coId}/reject`, {}, { withCredentials: true }); refresh(); };
  const deleteRFI = async (id) => { if (!window.confirm('Eliminar?')) return; await axios.delete(`${API_URL}/api/construction/rfis/${id}`, { withCredentials: true }); refresh(); };
  const deleteCO = async (id) => { if (!window.confirm('Eliminar?')) return; await axios.delete(`${API_URL}/api/construction/change-orders/${id}`, { withCredentials: true }); refresh(); };

  const totalCOAmount = cos.filter(c => c.status === 'approved').reduce((s, c) => s + (c.amount || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
          <button onClick={() => setActiveView('rfis')} className={`px-4 py-2 rounded-md text-sm font-medium ${activeView === 'rfis' ? 'bg-white shadow text-blue-600' : 'text-slate-600'}`} data-testid="view-rfis">RFIs ({rfis.length})</button>
          <button onClick={() => setActiveView('cos')} className={`px-4 py-2 rounded-md text-sm font-medium ${activeView === 'cos' ? 'bg-white shadow text-blue-600' : 'text-slate-600'}`} data-testid="view-cos">Change Orders ({cos.length})</button>
        </div>
        <div className="flex gap-2">
          {activeView === 'rfis' ? (
            <button onClick={() => setShowRFI(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid="add-rfi-btn"><Plus size={18} /> Nuevo RFI</button>
          ) : (
            <button onClick={() => setShowCO(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid="add-co-btn"><Plus size={18} /> Nuevo CO</button>
          )}
        </div>
      </div>

      {totalCOAmount > 0 && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm"><Warning size={16} className="inline text-amber-600 mr-1" /> <strong>Change Orders Aprobados:</strong> ${totalCOAmount.toLocaleString()} impacto en presupuesto</div>}

      {showRFI && <RFIForm projectId={projectId} onClose={() => setShowRFI(false)} onSave={() => { setShowRFI(false); refresh(); }} />}
      {showCO && <COForm projectId={projectId} onClose={() => setShowCO(false)} onSave={() => { setShowCO(false); refresh(); }} />}

      {loading ? <div className="text-center py-8"><CircleNotch size={30} className="animate-spin text-blue-600 mx-auto" /></div> :
        activeView === 'rfis' ? (
          rfis.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500"><FileText size={36} className="mx-auto mb-2 text-slate-300" /><p className="text-sm">No hay RFIs</p></div> :
          <div className="space-y-3">{rfis.map(rfi => <RFICard key={rfi.id} rfi={rfi} onRespond={respondRFI} onConvert={convertToCO} onDelete={deleteRFI} />)}</div>
        ) : (
          cos.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500"><Receipt size={36} className="mx-auto mb-2 text-slate-300" /><p className="text-sm">No hay Change Orders</p></div> :
          <div className="space-y-3">{cos.map(co => <COCard key={co.id} co={co} onApprove={approveCO} onReject={rejectCO} onDelete={deleteCO} />)}</div>
        )}
    </div>
  );
}

const RFI_PRIORITY = { low: { l: 'Baja', c: 'bg-slate-100 text-slate-600' }, medium: { l: 'Media', c: 'bg-amber-100 text-amber-700' }, high: { l: 'Alta', c: 'bg-orange-100 text-orange-700' }, urgent: { l: 'Urgente', c: 'bg-red-100 text-red-700' } };
const RFI_STATUS = { open: { l: 'Abierto', c: 'bg-blue-100 text-blue-700' }, answered: { l: 'Respondido', c: 'bg-emerald-100 text-emerald-700' }, closed: { l: 'Cerrado', c: 'bg-slate-100 text-slate-600' }, converted_to_co: { l: 'Convertido a CO', c: 'bg-violet-100 text-violet-700' } };

function RFICard({ rfi, onRespond, onConvert, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [response, setResponse] = useState('');
  const [showConvert, setShowConvert] = useState(false);
  const [coAmount, setCOAmount] = useState(0);
  const [coDays, setCODays] = useState(0);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`rfi-card-${rfi.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"><FileText size={20} className="text-blue-600" /></div>
          <div>
            <div className="flex items-center gap-2"><span className="font-semibold text-slate-900">{rfi.rfi_number}</span><span className={`text-xs px-2 py-0.5 rounded-full ${RFI_STATUS[rfi.status]?.c}`}>{RFI_STATUS[rfi.status]?.l}</span><span className={`text-xs px-2 py-0.5 rounded-full ${RFI_PRIORITY[rfi.priority]?.c}`}>{RFI_PRIORITY[rfi.priority]?.l}</span></div>
            <p className="text-sm text-slate-700 font-medium">{rfi.subject}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {rfi.due_date && <span className="text-xs text-slate-400 mr-2">{rfi.due_date}</span>}
          <button onClick={() => setExpanded(!expanded)} className="p-1.5 hover:bg-slate-100 rounded">{expanded ? <CaretUp size={16} /> : <CaretDown size={16} />}</button>
          <button onClick={() => onDelete(rfi.id)} className="p-1.5 text-red-400 hover:bg-red-50 rounded"><Trash size={16} /></button>
        </div>
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t space-y-3">
          <p className="text-sm text-slate-600"><strong>Descripcion:</strong> {rfi.description}</p>
          <p className="text-xs text-slate-400">Enviado por: {rfi.submitted_by}{rfi.assigned_to ? ` | Asignado a: ${rfi.assigned_to}` : ''}</p>
          {rfi.response && <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm"><strong className="text-emerald-700">Respuesta ({rfi.response_by}):</strong><p className="text-emerald-600 mt-1">{rfi.response}</p></div>}
          {rfi.status === 'open' && (
            <div className="flex gap-2"><input value={response} onChange={e => setResponse(e.target.value)} className="flex-1 border rounded px-3 py-2 text-sm" placeholder="Escribir respuesta..." /><button onClick={() => { onRespond(rfi.id, response); setResponse(''); }} className="px-4 py-2 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700">Responder</button></div>
          )}
          {(rfi.status === 'open' || rfi.status === 'answered') && !showConvert && (
            <button onClick={() => setShowConvert(true)} className="text-sm text-violet-600 hover:text-violet-800 flex items-center gap-1"><ArrowRight size={14} /> Convertir a Change Order</button>
          )}
          {showConvert && (
            <div className="bg-violet-50 border border-violet-200 rounded p-3 flex items-end gap-3">
              <div><label className="text-xs text-violet-600 block mb-1">Monto ($)</label><input type="number" value={coAmount} onChange={e => setCOAmount(parseFloat(e.target.value)||0)} className="border rounded px-2 py-1.5 text-sm w-28" /></div>
              <div><label className="text-xs text-violet-600 block mb-1">Dias Impacto</label><input type="number" value={coDays} onChange={e => setCODays(parseInt(e.target.value)||0)} className="border rounded px-2 py-1.5 text-sm w-20" /></div>
              <button onClick={() => { onConvert(rfi.id, coAmount, coDays, rfi.description); setShowConvert(false); }} className="px-3 py-1.5 bg-violet-600 text-white rounded text-sm">Crear CO</button>
              <button onClick={() => setShowConvert(false)} className="px-3 py-1.5 text-slate-500 text-sm">Cancelar</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const CO_STATUS = { draft: { l: 'Borrador', c: 'bg-slate-100 text-slate-600' }, submitted: { l: 'Enviado', c: 'bg-blue-100 text-blue-700' }, approved: { l: 'Aprobado', c: 'bg-emerald-100 text-emerald-700' }, rejected: { l: 'Rechazado', c: 'bg-red-100 text-red-700' } };

function COCard({ co, onApprove, onReject, onDelete }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`co-card-${co.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center"><Receipt size={20} className="text-amber-600" /></div>
          <div>
            <div className="flex items-center gap-2"><span className="font-semibold text-slate-900">{co.co_number}</span><span className={`text-xs px-2 py-0.5 rounded-full ${CO_STATUS[co.status]?.c}`}>{CO_STATUS[co.status]?.l}</span>{co.rfi_number && <span className="text-xs text-violet-600">de {co.rfi_number}</span>}</div>
            <p className="text-sm text-slate-700">{co.description}</p>
            {co.reason && <p className="text-xs text-slate-500 mt-0.5">{co.reason}</p>}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className={`text-lg font-bold ${co.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>${co.amount?.toLocaleString()}</p>
            {co.impact_days > 0 && <p className="text-xs text-slate-500">+{co.impact_days} dias</p>}
          </div>
          <div className="flex items-center gap-1">
            {(co.status === 'draft' || co.status === 'submitted') && <>
              <button onClick={() => onApprove(co.id)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded" title="Aprobar"><CheckCircle size={18} /></button>
              <button onClick={() => onReject(co.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Rechazar"><X size={18} /></button>
            </>}
            <button onClick={() => onDelete(co.id)} className="p-1.5 text-red-400 hover:bg-red-50 rounded"><Trash size={16} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RFIForm({ projectId, onClose, onSave }) {
  const [form, setForm] = useState({ subject: '', description: '', priority: 'medium', due_date: '' });
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!form.subject.trim()) return alert('Asunto requerido');
    setSaving(true);
    try { await axios.post(`${API_URL}/api/construction/rfis`, { ...form, project_id: projectId }, { withCredentials: true }); onSave(); } catch { alert('Error'); } finally { setSaving(false); }
  };
  return (
    <div className="bg-white rounded-xl border border-blue-200 p-5 space-y-3" data-testid="rfi-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><label className="block text-xs text-slate-500 mb-1">Asunto *</label><input value={form.subject} onChange={e => setForm(f => ({...f, subject: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm" placeholder="Ej: Ubicacion de bandeja de cables en piso 3" data-testid="rfi-subject" /></div>
        <div className="col-span-2"><label className="block text-xs text-slate-500 mb-1">Descripcion *</label><textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={3} className="w-full border rounded px-3 py-2 text-sm" placeholder="Detalle de la solicitud de informacion..." data-testid="rfi-description" /></div>
        <div><label className="block text-xs text-slate-500 mb-1">Prioridad</label><select value={form.priority} onChange={e => setForm(f => ({...f, priority: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm"><option value="low">Baja</option><option value="medium">Media</option><option value="high">Alta</option><option value="urgent">Urgente</option></select></div>
        <div><label className="block text-xs text-slate-500 mb-1">Fecha Limite</label><input type="date" value={form.due_date} onChange={e => setForm(f => ({...f, due_date: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm" /></div>
      </div>
      <div className="flex justify-end gap-2"><button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded">Cancelar</button><button onClick={handleSave} disabled={saving} className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50" data-testid="save-rfi-btn">{saving ? 'Guardando...' : 'Crear RFI'}</button></div>
    </div>
  );
}

function COForm({ projectId, onClose, onSave }) {
  const [form, setForm] = useState({ description: '', reason: '', amount: 0, impact_days: 0 });
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!form.description.trim()) return alert('Descripcion requerida');
    setSaving(true);
    try { await axios.post(`${API_URL}/api/construction/change-orders`, { ...form, project_id: projectId }, { withCredentials: true }); onSave(); } catch { alert('Error'); } finally { setSaving(false); }
  };
  return (
    <div className="bg-white rounded-xl border border-blue-200 p-5 space-y-3" data-testid="co-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><label className="block text-xs text-slate-500 mb-1">Descripcion *</label><input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="w-full border rounded px-3 py-2 text-sm" placeholder="Descripcion del cambio" data-testid="co-description" /></div>
        <div className="col-span-2"><label className="block text-xs text-slate-500 mb-1">Razon</label><textarea value={form.reason} onChange={e => setForm(f => ({...f, reason: e.target.value}))} rows={2} className="w-full border rounded px-3 py-2 text-sm" placeholder="Razon del cambio..." /></div>
        <div><label className="block text-xs text-slate-500 mb-1">Monto ($)</label><input type="number" value={form.amount} onChange={e => setForm(f => ({...f, amount: parseFloat(e.target.value)||0}))} className="w-full border rounded px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs text-slate-500 mb-1">Dias de Impacto</label><input type="number" value={form.impact_days} onChange={e => setForm(f => ({...f, impact_days: parseInt(e.target.value)||0}))} className="w-full border rounded px-3 py-2 text-sm" /></div>
      </div>
      <div className="flex justify-end gap-2"><button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded">Cancelar</button><button onClick={handleSave} disabled={saving} className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50" data-testid="save-co-btn">{saving ? 'Guardando...' : 'Crear CO'}</button></div>
    </div>
  );
}

// ==================== PROGRESS BILLING TAB ====================
export function ProgressBillingTab({ project, onRefresh, projectId }) {
  const [bills, setBills] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/construction/progress-billing?project_id=${projectId}`, { withCredentials: true })
      .then(r => setBills(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [projectId]);

  const refresh = async () => {
    const r = await axios.get(`${API_URL}/api/construction/progress-billing?project_id=${projectId}`, { withCredentials: true });
    setBills(r.data);
  };

  const createBill = async (data) => {
    await axios.post(`${API_URL}/api/construction/progress-billing`, { ...data, project_id: projectId }, { withCredentials: true });
    setShowCreate(false);
    refresh();
  };

  const submitBill = async (id) => { await axios.post(`${API_URL}/api/construction/progress-billing/${id}/submit`, {}, { withCredentials: true }); refresh(); };
  const approveBill = async (id) => { await axios.post(`${API_URL}/api/construction/progress-billing/${id}/approve`, {}, { withCredentials: true }); refresh(); };
  const deleteBill = async (id) => { if (!window.confirm('Eliminar?')) return; await axios.delete(`${API_URL}/api/construction/progress-billing/${id}`, { withCredentials: true }); refresh(); };

  const totalBilled = bills.filter(b => ['submitted','approved','paid'].includes(b.status)).reduce((s, b) => s + (b.total_due || 0), 0);
  const contractTotal = project.budget?.total || 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-4">
          <div className="bg-white rounded-xl border border-slate-200 px-4 py-3"><p className="text-xs text-slate-500">Contrato</p><p className="text-xl font-bold text-slate-700">${contractTotal.toLocaleString()}</p></div>
          <div className="bg-white rounded-xl border border-slate-200 px-4 py-3"><p className="text-xs text-slate-500">Facturado</p><p className="text-xl font-bold text-emerald-600">${totalBilled.toLocaleString()}</p></div>
          <div className="bg-white rounded-xl border border-slate-200 px-4 py-3"><p className="text-xs text-slate-500">Balance</p><p className="text-xl font-bold text-blue-600">${(contractTotal - totalBilled).toLocaleString()}</p></div>
        </div>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid="create-billing-btn"><Plus size={18} /> Nueva Facturacion AIA</button>
      </div>

      {showCreate && <BillingCreateForm project={project} onSave={createBill} onClose={() => setShowCreate(false)} />}

      {loading ? <div className="text-center py-8"><CircleNotch size={30} className="animate-spin text-blue-600 mx-auto" /></div> :
        bills.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500"><CurrencyDollar size={36} className="mx-auto mb-2 text-slate-300" /><p className="text-sm">No hay facturaciones AIA</p></div> :
        <div className="space-y-4">{bills.map(b => <BillingCard key={b.id} bill={b} onSubmit={submitBill} onApprove={approveBill} onDelete={deleteBill} />)}</div>}
    </div>
  );
}

const BILL_STATUS = { draft: { l: 'Borrador', c: 'bg-slate-100 text-slate-600' }, submitted: { l: 'Enviado', c: 'bg-blue-100 text-blue-700' }, approved: { l: 'Aprobado', c: 'bg-emerald-100 text-emerald-700' }, paid: { l: 'Pagado', c: 'bg-green-100 text-green-700' } };

function BillingCard({ bill, onSubmit, onApprove, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const s = BILL_STATUS[bill.status] || BILL_STATUS.draft;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`billing-card-${bill.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center"><CurrencyDollar size={20} className="text-emerald-600" /></div>
          <div>
            <div className="flex items-center gap-2"><span className="font-semibold text-slate-900">{bill.billing_number}</span><span className={`text-xs px-2 py-0.5 rounded-full ${s.c}`}>{s.l}</span></div>
            <p className="text-xs text-slate-500">{bill.period_start && bill.period_end ? `${bill.period_start} a ${bill.period_end}` : 'Sin periodo'} | Retencion: {bill.retention_rate}%</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right"><p className="text-lg font-bold text-emerald-600">${bill.total_due?.toLocaleString()}</p><p className="text-xs text-slate-400">Retencion: ${bill.retention_amount?.toLocaleString()}</p></div>
          <div className="flex items-center gap-1">
            {bill.status === 'draft' && <button onClick={() => onSubmit(bill.id)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Enviar"><PaperPlaneTilt size={16} /></button>}
            {bill.status === 'submitted' && <button onClick={() => onApprove(bill.id)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded" title="Aprobar"><CheckCircle size={18} /></button>}
            <button onClick={() => setExpanded(!expanded)} className="p-1.5 hover:bg-slate-100 rounded">{expanded ? <CaretUp size={16} /> : <CaretDown size={16} />}</button>
            <button onClick={() => onDelete(bill.id)} className="p-1.5 text-red-400 hover:bg-red-50 rounded"><Trash size={16} /></button>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t">
          <table className="w-full text-sm">
            <thead className="bg-slate-50"><tr><th className="text-left p-2">Fase</th><th className="text-right p-2">Contrato</th><th className="text-center p-2">% Anterior</th><th className="text-center p-2">% Actual</th><th className="text-right p-2">Este Periodo</th><th className="text-right p-2">Total Facturado</th><th className="text-right p-2">Balance</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {(bill.line_items || []).map((li, i) => (
                <tr key={i}><td className="p-2">{li.phase_name}</td><td className="p-2 text-right">${li.contract_amount?.toLocaleString()}</td><td className="p-2 text-center">{li.prev_billed_pct}%</td><td className="p-2 text-center font-semibold text-blue-600">{li.current_pct}%</td><td className="p-2 text-right text-emerald-600">${li.current_amount?.toLocaleString()}</td><td className="p-2 text-right">${li.total_billed?.toLocaleString()}</td><td className="p-2 text-right">${li.balance?.toLocaleString()}</td></tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50 font-semibold"><tr><td className="p-2" colSpan={4}>TOTAL</td><td className="p-2 text-right text-emerald-700">${bill.subtotal?.toLocaleString()}</td><td colSpan={2}></td></tr></tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

function BillingCreateForm({ project, onSave, onClose }) {
  const phases = project.phases || [];
  const [lineItems, setLineItems] = useState(phases.map(ph => ({ phase_id: ph.id, phase_name: ph.name, contract_amount: ph.budget || 0, prev_billed_pct: 0, current_pct: ph.progress || 0 })));
  const [retentionRate, setRetentionRate] = useState(10);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [saving, setSaving] = useState(false);

  const updatePct = (i, val) => { const u = [...lineItems]; u[i] = { ...u[i], current_pct: parseFloat(val) || 0 }; setLineItems(u); };

  const subtotal = lineItems.reduce((s, li) => { const increment = Math.max(li.current_pct - li.prev_billed_pct, 0); return s + (li.contract_amount * increment / 100); }, 0);
  const retention = subtotal * retentionRate / 100;

  const handleSave = async () => {
    setSaving(true);
    try { await onSave({ line_items: lineItems, retention_rate: retentionRate, period_start: periodStart, period_end: periodEnd }); } catch { alert('Error'); } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-blue-200 p-5 space-y-4" data-testid="billing-form">
      <h3 className="font-semibold text-slate-900">Nueva Facturacion AIA</h3>
      <div className="grid grid-cols-3 gap-3">
        <div><label className="block text-xs text-slate-500 mb-1">Periodo Inicio</label><input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs text-slate-500 mb-1">Periodo Fin</label><input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs text-slate-500 mb-1">Retencion (%)</label><input type="number" value={retentionRate} onChange={e => setRetentionRate(parseFloat(e.target.value)||0)} className="w-full border rounded px-3 py-2 text-sm" /></div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50"><tr><th className="text-left p-2">Fase</th><th className="text-right p-2">Contrato</th><th className="text-center p-2">% Anterior</th><th className="text-center p-2">% Actual</th><th className="text-right p-2">Este Periodo</th></tr></thead>
        <tbody className="divide-y divide-slate-100">{lineItems.map((li, i) => {
          const increment = Math.max(li.current_pct - li.prev_billed_pct, 0);
          const amount = li.contract_amount * increment / 100;
          return (<tr key={i}><td className="p-2">{li.phase_name}</td><td className="p-2 text-right">${li.contract_amount.toLocaleString()}</td><td className="p-2 text-center text-slate-400">{li.prev_billed_pct}%</td><td className="p-2 text-center"><input type="number" min="0" max="100" value={li.current_pct} onChange={e => updatePct(i, e.target.value)} className="w-16 border rounded px-2 py-1 text-center text-sm" />%</td><td className="p-2 text-right text-emerald-600 font-medium">${amount.toFixed(2)}</td></tr>);
        })}</tbody>
      </table>
      <div className="text-right space-y-1 text-sm"><p>Subtotal: <strong>${subtotal.toFixed(2)}</strong></p><p>Retencion ({retentionRate}%): <strong className="text-amber-600">-${retention.toFixed(2)}</strong></p><p className="text-lg">Total Due: <strong className="text-emerald-700">${(subtotal - retention).toFixed(2)}</strong></p></div>
      <div className="flex justify-end gap-2"><button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded">Cancelar</button><button onClick={handleSave} disabled={saving} className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50" data-testid="save-billing-btn">{saving ? 'Guardando...' : 'Crear Facturacion'}</button></div>
    </div>
  );
}

// ==================== GC SUMMARY EMAIL ====================
export function GCSummaryButton({ projectId }) {
  const [show, setShow] = useState(false);
  const [gcEmail, setGCEmail] = useState('');
  const [gcName, setGCName] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!gcEmail) return alert('Email del GC requerido');
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/construction/projects/${projectId}/send-gc-summary`, { gc_email: gcEmail, gc_name: gcName }, { withCredentials: true });
      alert('Reporte semanal enviado exitosamente');
      setShow(false);
    } catch (err) { alert(err.response?.data?.detail || 'Error enviando'); }
    finally { setSending(false); }
  };

  return (
    <>
      <button onClick={() => setShow(true)} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700" data-testid="send-gc-summary-btn">
        <Envelope size={18} /> Reporte al GC
      </button>
      {show && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5" data-testid="gc-summary-modal">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Enviar Reporte Semanal al GC</h3>
            <p className="text-sm text-slate-500 mb-4">Se enviara un resumen completo con avance por fase, horas, costos, RFIs y Change Orders pendientes.</p>
            <div className="space-y-3">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Nombre del GC</label><input value={gcName} onChange={e => setGCName(e.target.value)} className="w-full border rounded-lg px-3 py-2" placeholder="Ej: John Smith - Turner Construction" data-testid="gc-name-input" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Email del GC *</label><input type="email" value={gcEmail} onChange={e => setGCEmail(e.target.value)} className="w-full border rounded-lg px-3 py-2" placeholder="gc@construction.com" data-testid="gc-email-input" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShow(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
              <button onClick={handleSend} disabled={sending} className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2" data-testid="confirm-send-gc-btn">
                {sending ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} />} Enviar Reporte
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
